import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  FlashcardState,
  FlashcardDeck,
  Flashcard,
  StudySession,
  CardProgress,
  FlashcardStats,
  StudyMode,
  FlashcardFilter,
  FlashcardSearchResult,
  CreateFlashcardRequest,
  CreateDeckRequest,
  UpdateCardProgressRequest,
  StudySessionResult,
  SpacedRepetitionResult
} from '../../types/flashcard'
import * as flashcardService from '../../services/flashcardService'

// Initial state
const initialState: FlashcardState = {
  decks: [],
  currentDeck: null,
  currentCard: null,
  currentSession: null,
  cardProgress: {},
  stats: null,
  studyMode: {
    type: 'detailed',
    showHints: true,
    showExplanations: true,
    autoFlip: false,
    autoFlipDelay: 3,
    shuffleCards: true,
    reviewIncorrect: true
  },
  filter: {},
  isLoading: false,
  error: null,
  searchResults: null,
  reviewCards: [],
  incorrectCards: [],
  reviewCardsLoading: false,
  incorrectCardsLoading: false
}

// Async thunks
export const fetchDecks = createAsyncThunk(
  'flashcard/fetchDecks',
  async (filter?: FlashcardFilter, { rejectWithValue }) => {
    try {
      return await flashcardService.getDecks(filter)
    } catch (error) {
      console.error('🚨 Redux fetchDecks error:', error)
      
      // Handle critical Firestore errors
      if (error instanceof Error && (
        error.message.includes('INTERNAL ASSERTION FAILED') ||
        error.message.includes('Unexpected state') ||
        error.message.includes('client has already been terminated')
      )) {
        console.warn('🔄 Critical Firestore error in Redux fetchDecks')
        return rejectWithValue({
          message: 'Firestore bağlantı hatası - sayfa yenilenecek',
          critical: true,
          originalError: error.message
        })
      }
      
      return rejectWithValue({
        message: error instanceof Error ? error.message : 'Bilinmeyen hata',
        critical: false
      })
    }
  }
)

export const fetchDeckById = createAsyncThunk(
  'flashcard/fetchDeckById',
  async (deckId: string, { rejectWithValue }) => {
    try {
      const deck = await flashcardService.getDeckById(deckId)
      return deck
    } catch (error) {
      console.error('🚨 Redux fetchDeckById error:', error)
      
      // Handle critical Firestore errors
      if (error instanceof Error && (
        error.message.includes('INTERNAL ASSERTION FAILED') ||
        error.message.includes('Unexpected state') ||
        error.message.includes('client has already been terminated')
      )) {
        console.warn('🔄 Critical Firestore error in Redux fetchDeckById')
        return rejectWithValue({
          message: 'Firestore bağlantı hatası - ana sayfaya yönlendirilecek',
          critical: true,
          originalError: error.message
        })
      }
      
      return rejectWithValue({
        message: error instanceof Error ? error.message : 'Bilinmeyen hata',
        critical: false
      })
    }
  }
)

export const createDeck = createAsyncThunk(
  'flashcard/createDeck',
  async (deckData: CreateDeckRequest) => {
    return await flashcardService.createDeck(deckData)
  }
)

export const updateDeck = createAsyncThunk(
  'flashcard/updateDeck',
  async ({ deckId, updates }: { deckId: string; updates: Partial<FlashcardDeck> }) => {
    return await flashcardService.updateDeck(deckId, updates)
  }
)

export const deleteDeck = createAsyncThunk(
  'flashcard/deleteDeck',
  async (deckId: string) => {
    await flashcardService.deleteDeck(deckId)
    return deckId
  }
)

export const createCard = createAsyncThunk(
  'flashcard/createCard',
  async (cardData: CreateFlashcardRequest) => {
    return await flashcardService.createCard(cardData)
  }
)

export const updateCard = createAsyncThunk(
  'flashcard/updateCard',
  async ({ cardId, updates }: { cardId: string; updates: Partial<Flashcard> }) => {
    return await flashcardService.updateCard(cardId, updates)
  }
)

export const deleteCard = createAsyncThunk(
  'flashcard/deleteCard',
  async ({ deckId, cardId }: { deckId: string; cardId: string }) => {
    await flashcardService.deleteCard(cardId)
    return { deckId, cardId }
  }
)

export const startStudySession = createAsyncThunk(
  'flashcard/startStudySession',
  async ({ deckId, mode }: { deckId: string; mode: StudyMode }) => {
    return await flashcardService.startStudySession(deckId, mode)
  }
)

export const updateCardProgress = createAsyncThunk(
  'flashcard/updateCardProgress',
  async (progressData: UpdateCardProgressRequest) => {
    return await flashcardService.updateCardProgress(progressData)
  }
)

export const completeStudySession = createAsyncThunk(
  'flashcard/completeStudySession',
  async (sessionId: string) => {
    return await flashcardService.completeStudySession(sessionId)
  }
)

export const fetchStats = createAsyncThunk(
  'flashcard/fetchStats',
  async () => {
    return await flashcardService.getStats()
  }
)

export const searchFlashcards = createAsyncThunk(
  'flashcard/searchFlashcards',
  async ({ query, filter, page = 1, limit = 20 }: {
    query: string
    filter?: FlashcardFilter
    page?: number
    limit?: number
  }, { getState }) => {
    const state = getState() as any
    const existingDecks = state.flashcard.decks
    return await flashcardService.searchFlashcards(query, filter, page, limit, existingDecks)
  }
)

export const getReviewCards = createAsyncThunk(
  'flashcard/getReviewCards',
  async () => {
    return await flashcardService.getReviewCards()
  }
)

export const getIncorrectCardsByDeck = createAsyncThunk(
  'flashcard/getIncorrectCardsByDeck',
  async ({ userId, deckId }: { userId: string; deckId: string }) => {
    return await flashcardService.getIncorrectCardsByDeck(deckId)
  }
)

export const getReviewCardsByDeck = createAsyncThunk(
  'flashcard/getReviewCardsByDeck',
  async ({ userId, deckId }: { userId: string; deckId: string }) => {
    return await flashcardService.getReviewCardsByDeck(deckId)
  }
)

export const saveIncorrectCard = createAsyncThunk(
  'flashcard/saveIncorrectCard',
  async ({ deckId, cardId, sessionId }: {
    deckId: string;
    cardId: string;
    sessionId?: string;
  }) => {
    await flashcardService.saveIncorrectCard(cardId, deckId, sessionId)
    return { deckId, cardId, sessionId }
  }
)

export const saveReviewCard = createAsyncThunk(
  'flashcard/saveReviewCard',
  async ({ deckId, cardId, sessionId }: {
    deckId: string;
    cardId: string;
    sessionId?: string;
  }) => {
    await flashcardService.saveReviewCard(cardId, deckId, sessionId)
    return { deckId, cardId, sessionId }
  }
)

// Slice
const flashcardSlice = createSlice({
  name: 'flashcard',
  initialState,
  reducers: {
    setCurrentDeck: (state, action: PayloadAction<FlashcardDeck | null>) => {
      state.currentDeck = action.payload
    },
    setCurrentCard: (state, action: PayloadAction<Flashcard | null>) => {
      state.currentCard = action.payload
    },
    setStudyMode: (state, action: PayloadAction<StudyMode>) => {
      state.studyMode = action.payload
    },
    setFilter: (state, action: PayloadAction<FlashcardFilter>) => {
      state.filter = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    clearSearchResults: (state) => {
      state.searchResults = null
    },
    updateLocalCardProgress: (state, action: PayloadAction<CardProgress>) => {
      state.cardProgress[action.payload.cardId] = action.payload
    },
    shuffleCurrentDeck: (state) => {
      if (state.currentDeck?.cards) {
        const shuffled = [...state.currentDeck.cards]
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1))
          ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
        }
        state.currentDeck.cards = shuffled
      }
    },
    resetCurrentSession: (state) => {
      state.currentSession = null
      state.currentCard = null
    },
    updateSessionProgress: (state, action: PayloadAction<{
      cardsStudied: number
      cardsCorrect: number
      cardsIncorrect: number
    }>) => {
      if (state.currentSession) {
        state.currentSession.cardsStudied = action.payload.cardsStudied
        state.currentSession.cardsCorrect = action.payload.cardsCorrect
        state.currentSession.cardsIncorrect = action.payload.cardsIncorrect
      }
    },
    resetReviewData: (state) => {
      state.reviewCards = []
      state.incorrectCards = []
      state.reviewCardsLoading = false
      state.incorrectCardsLoading = false
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch decks
      .addCase(fetchDecks.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchDecks.fulfilled, (state, action) => {
        state.isLoading = false
        state.decks = action.payload
      })
      .addCase(fetchDecks.rejected, (state, action) => {
        state.isLoading = false
        
        // Handle critical errors from rejectWithValue
        if (action.payload && typeof action.payload === 'object' && 'critical' in action.payload) {
          const payload = action.payload as { message: string; critical: boolean; originalError?: string }
          state.error = payload.message
          
          if (payload.critical) {
            console.error('🚨 Critical Firestore error in Redux - triggering recovery')
            // The component will handle the recovery based on the error state
          }
        } else {
          state.error = action.error.message || 'Desteler yüklenirken hata oluştu'
        }
      })
      
      // Fetch deck by ID
      .addCase(fetchDeckById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchDeckById.fulfilled, (state, action) => {
        state.isLoading = false
        console.log('flashcardSlice - fetchDeckById.fulfilled:', {
          deckId: action.payload.id,
          title: action.payload.title,
          totalCards: action.payload.cards.length
        })
        state.currentDeck = action.payload
      })
      .addCase(fetchDeckById.rejected, (state, action) => {
        state.isLoading = false
        
        // Handle critical errors from rejectWithValue
        if (action.payload && typeof action.payload === 'object' && 'critical' in action.payload) {
          const payload = action.payload as { message: string; critical: boolean; originalError?: string }
          state.error = payload.message
          
          if (payload.critical) {
            console.error('🚨 Critical Firestore error in Redux fetchDeckById - triggering recovery')
            // The component will handle the recovery based on the error state
          }
        } else {
          state.error = action.error.message || 'Deste yüklenirken hata oluştu'
        }
      })
      
      // Create deck
      .addCase(createDeck.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createDeck.fulfilled, (state, action) => {
        state.isLoading = false
        state.decks.push(action.payload)
      })
      .addCase(createDeck.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Deste oluşturulurken hata oluştu'
      })
      
      // Update deck
      .addCase(updateDeck.pending, (state) => {
        state.error = null
      })
      .addCase(updateDeck.fulfilled, (state, action) => {
        const index = state.decks.findIndex(deck => deck.id === action.payload.id)
        if (index !== -1) {
          state.decks[index] = action.payload
        }
        if (state.currentDeck?.id === action.payload.id) {
          state.currentDeck = action.payload
        }
      })
      .addCase(updateDeck.rejected, (state, action) => {
        state.error = action.error.message || 'Deste güncellenirken hata oluştu'
        console.error('updateDeck rejected:', action.error)
      })
      
      // Delete deck
      .addCase(deleteDeck.fulfilled, (state, action) => {
        state.decks = state.decks.filter(deck => deck.id !== action.payload)
        if (state.currentDeck?.id === action.payload) {
          state.currentDeck = null
        }
      })
      
      // Create card
      .addCase(createCard.pending, (state) => {
        state.error = null
      })
      .addCase(createCard.fulfilled, (state, action) => {
        // Sadece currentDeck'e ekle, decks array'ine ekleme
        // çünkü fetchDeckById ile tekrar yüklenecek
        if (state.currentDeck && state.currentDeck.id === action.payload.deckId) {
          // Kartın zaten mevcut olup olmadığını kontrol et
          const existingCardIndex = state.currentDeck.cards.findIndex(card => card.id === action.payload.id)
          if (existingCardIndex === -1) {
            state.currentDeck.cards.push(action.payload)
            state.currentDeck.totalCards += 1
          }
        }
      })
      .addCase(createCard.rejected, (state, action) => {
        state.error = action.error.message || 'Kart oluşturulurken hata oluştu'
        console.error('createCard rejected:', action.error)
      })
      
      // Update card
      .addCase(updateCard.pending, (state) => {
        state.error = null
      })
      .addCase(updateCard.fulfilled, (state, action) => {
        // Sadece currentDeck'i güncelle
        if (state.currentDeck && state.currentDeck.id === action.payload.deckId) {
          const cardIndex = state.currentDeck.cards.findIndex(card => card.id === action.payload.id)
          if (cardIndex !== -1) {
            state.currentDeck.cards[cardIndex] = action.payload
          }
        }
      })
      .addCase(updateCard.rejected, (state, action) => {
        state.error = action.error.message || 'Kart güncellenirken hata oluştu'
        console.error('updateCard rejected:', action.error)
      })
      
      // Delete card
      .addCase(deleteCard.fulfilled, (state, action) => {
        const { deckId, cardId } = action.payload
        if (state.currentDeck?.id === deckId) {
          state.currentDeck.cards = state.currentDeck.cards.filter(card => card.id !== cardId)
          state.currentDeck.totalCards -= 1
        }
        // Update deck in decks array
        const deckIndex = state.decks.findIndex(deck => deck.id === deckId)
        if (deckIndex !== -1) {
          state.decks[deckIndex].cards = state.decks[deckIndex].cards.filter(card => card.id !== cardId)
          state.decks[deckIndex].totalCards -= 1
        }
      })
      
      // Start study session
      .addCase(startStudySession.fulfilled, (state, action) => {
        state.currentSession = action.payload
      })
      
      // Update card progress
      .addCase(updateCardProgress.fulfilled, (state, action) => {
        state.cardProgress[action.payload.cardId] = action.payload
      })
      
      // Complete study session
      .addCase(completeStudySession.fulfilled, (state, action) => {
        state.currentSession = null
        // Update stats if needed
        if (state.stats) {
          state.stats.cardsStudied += action.payload.cardsStudied
          state.stats.totalStudyTime += Math.floor(action.payload.duration / 60)
        }
      })
      
      // Fetch stats
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.stats = action.payload
      })
      
      // Search flashcards
      .addCase(searchFlashcards.pending, (state) => {
        state.isLoading = true
      })
      .addCase(searchFlashcards.fulfilled, (state, action) => {
        state.isLoading = false
        state.searchResults = action.payload
      })
      .addCase(searchFlashcards.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Arama sırasında hata oluştu'
      })
      
      // Get review cards
      .addCase(getReviewCards.fulfilled, (state, action) => {
        // Create a special review deck
        state.currentDeck = {
          id: 'review-deck',
          title: 'Tekrar Kartları',
          description: 'Tekrar edilmesi gereken kartlar',
          subject: 'Karma',
          topic: 'Tekrar',
          unit: 'Genel',
          cards: action.payload,
          totalCards: action.payload.length,
          isPublic: false,
          isLocked: false,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: 'system'
        }
      })
      
      // Get incorrect cards by deck
      .addCase(getIncorrectCardsByDeck.pending, (state) => {
        state.incorrectCardsLoading = true
      })
      .addCase(getIncorrectCardsByDeck.fulfilled, (state, action) => {
        state.incorrectCardsLoading = false
        // Enrich cards with deckId from thunk args to ensure downstream usage (UI/save) has context
        const deckId = (action as any).meta?.arg?.deckId
        const enriched = Array.isArray(action.payload)
          ? action.payload.map(card => ({ ...card, deckId }))
          : []
        
        // Remove existing cards from this deck to avoid duplicates
        const filteredCards = state.incorrectCards.filter(card => (card as any).deckId !== deckId)
        state.incorrectCards = [...filteredCards, ...enriched]
      })
      .addCase(getIncorrectCardsByDeck.rejected, (state, action) => {
        state.incorrectCardsLoading = false
        state.error = action.error.message || 'Yanlış kartlar yüklenirken hata oluştu'
      })
      
      // Get review cards by deck
      .addCase(getReviewCardsByDeck.pending, (state) => {
        state.reviewCardsLoading = true
      })
      .addCase(getReviewCardsByDeck.fulfilled, (state, action) => {
        state.reviewCardsLoading = false
        // Enrich cards with deckId from thunk args to ensure downstream usage (UI/save) has context
        const deckId = (action as any).meta?.arg?.deckId
        const enriched = Array.isArray(action.payload)
          ? action.payload.map(card => ({ ...card, deckId }))
          : []
        
        // Remove existing cards from this deck to avoid duplicates
        const filteredCards = state.reviewCards.filter(card => (card as any).deckId !== deckId)
        state.reviewCards = [...filteredCards, ...enriched]
      })
      .addCase(getReviewCardsByDeck.rejected, (state, action) => {
        state.reviewCardsLoading = false
        state.error = action.error.message || 'Tekrar kartları yüklenirken hata oluştu'
      })
      
      // Save incorrect card
      .addCase(saveIncorrectCard.fulfilled, (state, action) => {
        // Card successfully saved to database - no need to update local state
        // as it will be fetched when user visits review page
        console.log('Incorrect card saved successfully')
      })
      .addCase(saveIncorrectCard.rejected, (state, action) => {
        state.error = action.error.message || 'Yanlış kart kaydedilirken hata oluştu'
      })
      
      // Save review card
      .addCase(saveReviewCard.fulfilled, (state, action) => {
        // Card successfully saved to database - no need to update local state
        // as it will be fetched when user visits review page
        console.log('Review card saved successfully')
      })
      .addCase(saveReviewCard.rejected, (state, action) => {
        state.error = action.error.message || 'Tekrar kartı kaydedilirken hata oluştu'
      })
  }
})

export const {
  setCurrentDeck,
  setCurrentCard,
  setStudyMode,
  setFilter,
  clearError,
  clearSearchResults,
  updateLocalCardProgress,
  shuffleCurrentDeck,
  resetCurrentSession,
  updateSessionProgress,
  resetReviewData
} = flashcardSlice.actions

export default flashcardSlice.reducer